import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Formik } from 'formik'
import { flatten, sortedUniq, uniqBy } from 'lodash'
import { useHistory } from 'react-router-dom'
import { endOfDay, isSameDay, startOfDay } from 'date-fns'

import BaseModal from 'components/Modals/BaseModal'
import { closeModal } from 'store/actions/modals'
import { Loading } from 'components/UI'
import { EventSummaryList } from 'components/Schedule'
import { Button, Select, Form, Checkbox } from 'components/Forms'
import { getFiltersForSearch } from 'services/Utilities'
import { Format, Parse } from 'services/Dates'
import { TrackAction } from 'services/TrackAction'
import useApi from 'components/Hooks/useApi'
import { Alert } from 'ui'

import { t } from 'translations'

const ContactListCreationModal = ({ ids }) => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { configs } = useSelector(state => state.resources)
  const { timeZone, defaultConfig } = useSelector(state => state.settings)
  const { 'filter-schedule': savedOptions } = useSelector(state => state.settings)

  const [errorLoading, setErrorLoading] = useState(false)

  // Queries definition
  const {
    isLoading: selectedEventsLoading,
    response: selectedEvents,
    handleApi: fetchSelectedEvents
  } = useApi({ method: 'POST', url: 'schedule/schedule/query', initialLoad: false })
  const {
    isLoading: otherEventsLoading,
    response: otherEvents,
    handleApi: fetchOtherEvents
  } = useApi({ method: 'POST', url: 'schedule/schedule/query', initialLoad: false })
  const {
    isLoading: contactListsLoading,
    response: lists,
    handleApi: fetchContactLists
  } = useApi({ url: 'contact/lists', autoExecute: false, initialLoad: false })
  const { handleApi: createListQuery } = useApi({ method: 'POST', url: 'contact/lists' })

  // Variables to warn the user in the modal that some shifts have been filtered out
  let date, validEvents, notAllOutOfList, notAllSameDay, rangeStart, rangeEnd

  // Fetch selected events
  useEffect(() => {
    const fetchEvents = async () => {
      const { response, isError } = await fetchSelectedEvents({ shiftIds: ids })
      if (isError || !response.length) return setErrorLoading(true)
    }
    if (ids.length) fetchEvents()
  }, [ids, fetchSelectedEvents])

  if (selectedEvents && selectedEvents.length) {
    let mainDate = Parse(selectedEvents[0].start)
    rangeStart = Format(startOfDay(mainDate), { outFormat: 'server' })
    rangeEnd = Format(endOfDay(Parse(mainDate, { inFormat: 'date' })), { outFormat: 'server' })
    date = Format(mainDate, { outFormat: 'date' })
    // Remove all shifts that are not the same day
    validEvents = selectedEvents.filter(event => {
      // TODO: Handle grouped lists
      const sameDay = isSameDay(mainDate, Parse(event.start))
      const notInList = typeof event.contactListId === 'undefined'

      if (!notAllSameDay) notAllSameDay = !sameDay
      if (!notAllOutOfList) notAllOutOfList = !notInList

      return sameDay && notInList
    })
  }

  // Show only unassigned shifts or timeOffs for the day, avoiding duplicating the already validEvents
  const otherEventsForTheDay =
    otherEvents?.length && validEvents?.length
      ? otherEvents.filter(({ employeeId, isOnTimeoff, isAbsent, contactListId }) => (!employeeId || isOnTimeoff || isAbsent) && !contactListId)
      : []

  // Fetch lists for the selected date
  useEffect(() => {
    const fetchLists = async () => {
      const { isError } = await fetchContactLists({ params: { rangeStart: date, rangeEnd: date, status: ['PAUSED', 'CREATED'] } })
      if (isError) setErrorLoading(true)
    }

    if (date) fetchLists()
  }, [date, fetchContactLists])

  // Fetch other unassigned shifts or timeOffs for the day
  useEffect(() => {
    const fetchEvents = async () => {
      const { isError } = await fetchOtherEvents({
        ...getFiltersForSearch(savedOptions || {}),
        rangeStart,
        rangeEnd,
        rangeCheckStartOnly: true,
        markShiftsForLeaveOrVacation: true
      })
      if (isError) setErrorLoading(true)
    }

    if (selectedEvents && selectedEvents?.length) fetchEvents()
  }, [selectedEvents, fetchOtherEvents, rangeEnd, rangeStart, savedOptions])

  const createList = async ({ mergeWith, shouldMerge, ...values }, actions) => {
    // Add all of the other unsassigned shifts and timeOffs if the checkbox is checked
    if (values.addOtherShifts) {
      values.shifts = uniqBy([...validEvents, ...otherEventsForTheDay], '_id').map(({ _id }) => _id)
    }
    // Only add the mergeWith if the shouldMerge is set
    if (shouldMerge) values.mergeWith = mergeWith
    const { response: listId, isError } = await createListQuery(values)

    if (isError) {
      actions.setStatus({ type: 'danger', msg: 'manager.contactLists.CREATE_LIST_MODAL.ERROR_CREATING_LIST' })
      actions.setSubmitting(false)
    } else {
      // Track that user has created a contact list
      TrackAction('CREATE_CONTACT_LIST')

      dispatch(closeModal())
      history.push(`/manage/contact/lists/${listId}`)
    }

    return listId
  }

  if (selectedEventsLoading || contactListsLoading || otherEventsLoading) return <Loading />

  if (errorLoading || !validEvents?.length || !configs?.length) {
    return (
      <BaseModal hideButtons title={`manager.contactLists.CREATE_LIST_MODAL`}>
        <Alert variant="warning" className="mt-2" show={!!errorLoading}>
          {t(`manager.contactLists.CREATE_LIST_MODAL.ERROR_LOADING`)}
        </Alert>
        <Alert variant="warning" className="mt-2" show={!validEvents?.length && !errorLoading}>
          {t(`manager.contactLists.CREATE_LIST_MODAL.ERROR_NO_VALID_EVENTS`)}
        </Alert>
        <Alert variant="warning" className="mt-2" show={!configs?.length}>
          {t(`manager.contactLists.CREATE_LIST_MODAL.ERROR_NO_AVAILABLE_CONFIGS`)}
        </Alert>
        <div className="mt-2">
          <Button text="actions.CLOSE" className="btn btn-outline-secondary" onClick={() => dispatch(closeModal())} />
        </div>
      </BaseModal>
    )
  }

  return (
    <BaseModal hideButtons title={`manager.contactLists.CREATE_LIST_MODAL`}>
      <Formik
        enableReinitialize={true}
        initialValues={{
          timeZone,
          shifts: validEvents.map(({ _id }) => _id),
          mergeWith: lists?.length ? lists[0]._id : '',
          shouldMerge: false,
          addOtherShifts: false,
          listConfigId: defaultConfig && defaultConfig !== 'noConfig' ? defaultConfig : configs[0]._id
        }}
        onSubmit={createList}
      >
        {({ values, isSubmitting, status, setStatus }) => {
          // Get the events to pass to the EventSummaryList component
          let eventsForContactList = validEvents
          if (values.addOtherShifts) {
            eventsForContactList = uniqBy([...eventsForContactList, ...otherEventsForTheDay], '_id')
          }
          return (
            <Form className="mt-2" noPrompt>
              {/* Error messages */}
              <Alert variant={status?.type} onClose={() => setStatus()} show={!!status?.msg}>
                {status?.msg}
              </Alert>
              <Alert variant="warning" className="mt-2" show={!!notAllSameDay}>
                {t(`manager.contactLists.CREATE_LIST_MODAL.NOT_ALL_SAME_DAY`)}
              </Alert>
              <Alert variant="warning" className="mt-2" show={!!notAllOutOfList}>
                {t(`manager.contactLists.CREATE_LIST_MODAL.NOT_ALL_OUT_OF_LIST`)}
              </Alert>

              {/* Show events that will end up in the list */}
              <EventSummaryList events={eventsForContactList} />

              {otherEventsForTheDay.length ? <Checkbox module="manager.contactLists.CREATE_LIST_MODAL" name="addOtherShifts" /> : null}

              <div className="mt-2">
                {/* If there is only one config, it will be selected by default */}
                {configs?.length > 1 ? (
                  <Select
                    module="manager.contactLists.CREATE_LIST_MODAL"
                    name="listConfigId"
                    options={configs.map(({ name, ...rest }) => ({ ...rest, name: name.default }))}
                    disabled={values.shouldMerge}
                  />
                ) : null}

                {/* Determines whether or not we are merging */}
                {lists?.length ? (
                  <>
                    <Checkbox module="manager.contactLists.CREATE_LIST_MODAL" name="shouldMerge" />
                    {values.shouldMerge ? (
                      <Select
                        module="manager.contactLists.CREATE_LIST_MODAL"
                        name="mergeWith"
                        options={lists.map(({ _id, validDates }) => ({
                          _id,
                          name: `(${_id.substring(20, 24).toUpperCase()}) ${sortedUniq(flatten(validDates))
                            .map(date => Format(date, { outFormat: 'humanDate' }))
                            .join(', ')}`
                        }))}
                      />
                    ) : null}
                  </>
                ) : null}
              </div>

              <div className="mt-3">
                <Button className="btn btn-primary mr-2" type="submit" isSubmitting={isSubmitting} text="SUBMIT" />
                <Button text="actions.CLOSE" className="btn btn-outline-secondary" onClick={() => dispatch(closeModal())} />
              </div>
            </Form>
          )
        }}
      </Formik>
    </BaseModal>
  )
}

export default ContactListCreationModal
